import { useState, useRef, useEffect } from 'react';
import { FiCheck } from 'react-icons/fi';
import { AssetGroupList } from './AssetGroupList';

/**
 * The active-session asset workspace: loading / empty states, the grouped asset
 * list (desktop two-pane + mobile collapsible groups), and the offline preload
 * progress bar.
 *
 * Owns the view-local UI state that only this subtree needs — which group is
 * selected (desktop pane) / expanded (mobile cards), the resizable left-pane
 * width, and the timer-reset confirmation target — plus the effects that keep
 * that state in sync with the current groups. The caller still owns the data
 * (grouped assets, preload progress) and the group-timer accessors.
 * Extracted verbatim from TestSessionView.
 */
export function SessionAssetPanel({
  siteId,
  sessionAssetType,
  isExitEmergency,
  assetsLoading,
  sessionAssetsPage,
  debouncedEquipmentCodeSearch,
  groupedAssets,
  autoPreloadEnabled,
  autoPreloading,
  autoPreloadLoaded,
  autoPreloadTotal,
  autoPreloadPercent,
  getTimerStatus,
  getTimerRemainingMs,
  getTimerStartedByName,
  getTimerStartedAt,
  startGroupTimer,
  resetGroupTimer,
  assetListProps,
}) {
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
  const [leftPaneWidthPx, setLeftPaneWidthPx] = useState(280);
  const [isResizing, setIsResizing] = useState(false);
  const [timerResetGroupName, setTimerResetGroupName] = useState(null);
  const workspaceRef = useRef(null);

  useEffect(() => {
    if (timerResetGroupName == null) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setTimerResetGroupName(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [timerResetGroupName]);

  // Resizer: drag to resize left pane (desktop)
  useEffect(() => {
    if (!isResizing) return;
    const minLeft = 200;
    const maxLeft = 520;
    const onMove = (e) => {
      const el = workspaceRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = typeof e.clientX === 'number' ? e.clientX : e.touches?.[0]?.clientX;
      if (x == null) return;
      const rel = Math.max(rect.left, Math.min(rect.right, x)) - rect.left;
      const w = Math.max(minLeft, Math.min(maxLeft, rel));
      setLeftPaneWidthPx(w);
    };
    const onEnd = () => setIsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isResizing]);

  // Clear selected group when asset type or site changes
  useEffect(() => {
    setSelectedGroupKey(null);
  }, [sessionAssetType, siteId]);

  useEffect(() => {
    if (!groupedAssets.length) {
      if (selectedGroupKey != null) setSelectedGroupKey(null);
      return;
    }
    if (!selectedGroupKey) {
      setSelectedGroupKey(groupedAssets[0].groupName);
      return;
    }
    const exists = groupedAssets.some((g) => g.groupName === selectedGroupKey);
    if (!exists) setSelectedGroupKey(groupedAssets[0].groupName);
  }, [groupedAssets, selectedGroupKey]);

  return (
    <div className="test-session-content test-list-container">
      {!siteId || !sessionAssetType ? (
        <p className="text-muted">Select a site and asset type to load assets.</p>
      ) : assetsLoading && sessionAssetsPage === 1 ? (
        <p className="text-muted">Loading assets…</p>
      ) : groupedAssets.length === 0 ? (
        <p className="empty-state">
          {debouncedEquipmentCodeSearch
            ? 'No assets found for this search and type.'
            : 'No assets found for this site and type.'}
        </p>
      ) : (
        <AssetGroupList
          groupedAssets={groupedAssets}
          sessionAssetType={sessionAssetType}
          isExitEmergency={isExitEmergency}
          workspaceRef={workspaceRef}
          leftPaneWidthPx={leftPaneWidthPx}
          isResizing={isResizing}
          setIsResizing={setIsResizing}
          selectedGroupKey={selectedGroupKey}
          setSelectedGroupKey={setSelectedGroupKey}
          expandedGroupKeys={expandedGroupKeys}
          setExpandedGroupKeys={setExpandedGroupKeys}
          timerResetGroupName={timerResetGroupName}
          setTimerResetGroupName={setTimerResetGroupName}
          getTimerStatus={getTimerStatus}
          getTimerRemainingMs={getTimerRemainingMs}
          getTimerStartedByName={getTimerStartedByName}
          getTimerStartedAt={getTimerStartedAt}
          startGroupTimer={startGroupTimer}
          resetGroupTimer={resetGroupTimer}
          assetListProps={assetListProps}
        />
      )}
      {groupedAssets.length > 0 && autoPreloadEnabled && (
        <div className="session-assets-pagination">
          {autoPreloading ? (
            <div className="session-preload">
              <div className="session-preload-row">
                <span className="text-muted">
                  Preloading assets for offline… {autoPreloadLoaded}/{autoPreloadTotal} ({autoPreloadPercent}%)
                </span>
              </div>
              <div className="session-preload-bar" role="progressbar" aria-valuenow={autoPreloadPercent} aria-valuemin={0} aria-valuemax={100}>
                <div className="session-preload-bar-fill" style={{ width: `${autoPreloadPercent}%` }} />
              </div>
            </div>
          ) : (
            <div className="session-preload session-preload-ready">
              <span className="session-preload-ready-chip">
                <FiCheck size={13} aria-hidden />
                Ready offline · {autoPreloadLoaded}/{autoPreloadTotal}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
